import * as React from "react";
import { useState, useEffect } from "react";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import { DatePicker } from "@mui/x-date-pickers";
import {
  createDateForPicker,
  handleDateChange
} from "../../utils/customC2CFormat";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import {
  Box,
  FormControlLabel,
  Radio,
  RadioGroup,
  Typography,
  Stack,
  Paper,
  InputAdornment,
  Tooltip,
  CircularProgress,
  Grid,
  Modal,
  Backdrop
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import InvoiceAPI from "../../services/InvoiceService";
import useAxiosPrivate from "../../hooks/useAxiosPrivate";
import useClient from "../../hooks/useClient";
import { useSnackbar } from "notistack";
import useLocales from "../../hooks/useLocales";

export default function RegenerateFormDialog({ onClose, row, onRefreshData }) {
  const getDefaultRadioValue = () => {
    if (row.invoice_type === "invoice_type_2") return "timesheet";
    return "milestone";
  };
  const [value, setValue] = React.useState(getDefaultRadioValue());
  const labelsConfig = JSON.parse(localStorage.getItem("labelsConfig") || "{}");
  const [amount, setAmount] = React.useState(null);
  const [isRefreshing, setIsRefreshing] = React.useState(false);
  const [isGenerating, setIsGenerating] = React.useState(false);
  const [approverNames, setApproverNames] = useState([]);
  const [nonSubmittedNames, setNonSubmittedNames] = useState([]);
  const [submittedNames, setSubmittedNames] = useState([]);
  const [invoiceData, setInvoiceData] = useState({});
  const axiosPrivate = useAxiosPrivate();
  const { selectedClient } = useClient();
  const { enqueueSnackbar } = useSnackbar();
  const { translate } = useLocales();
  useEffect(() => {
    console.log("******row", row);
  }, [row]);
  const [milestoneAmount, setMilestoneAmount] = useState(0);
  const [timesheetAmount, setTimesheetAmount] = useState(0);
  const [displayAmount, setDisplayAmount] = useState(row.c2c_timesheet_amount);

  // New state for date inputs
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [invoiceDate, setInvoiceDate] = useState("");
  const [isLoadingData, setIsLoadingData] = useState(false);
  const [showTimesheetData, setShowTimesheetData] = useState(false);
  const [showLoadingModal, setShowLoadingModal] = useState(false);

  useEffect(() => {
    if (row) {
      if (row.invoice_date) {
        setInvoiceDate(row.invoice_date);
      }

      if (row.invoice_start_date) {
        setStartDate(row.invoice_start_date);
      }

      if (row.invoice_end_date) {
        setEndDate(row.invoice_end_date);
      }
    }
  }, [row]);

  const [dateError, setDateError] = useState("");

  const regenerateInvoice = async (customPayload = null) => {
    setIsRefreshing(true);
    try {
      const payload = {
        contract_id: row.contract_uuid,
        start_date: row.invoice_start_date,
        end_date: row.invoice_end_date,
        invoice_date: row.invoice_date,
        regenerate: true
      };

      const response = await InvoiceAPI.FETCH_INVOICES(axiosPrivate, payload);
      setInvoiceData(response.data);
      setTimesheetAmount(response.data.c2c_timesheet_amount);
    } catch (error) {
      console.error("Error refreshing data:", error);
      enqueueSnackbar("Failed to fetch data. Please try again.", {
        variant: "error"
      });
    } finally {
      setIsRefreshing(false);
      setIsLoadingData(false);
      setShowLoadingModal(false); // Hide loading modal when done
    }
  };

  const handleRefreshClick = () => {
    regenerateInvoice();
  };

  const handleGetData = async () => {
    if (!startDate || !endDate) {
      enqueueSnackbar("Please select both start and end dates", {
        variant: "warning"
      });
      return;
    }
    // Validate date order before submit
    const s = startDate ? new Date(startDate) : null;
    const e = endDate ? new Date(endDate) : null;
    if (s && e && s > e) {
      setDateError("Start date cannot be after end date.");
      return;
    }
    if (s && e && e < s) {
      setDateError("End date cannot be before start date.");
      return;
    }
    setDateError("");

    setIsLoadingData(true);
    setShowLoadingModal(true); // Show loading modal
    // Set flag to show timesheet data after getting data
    setShowTimesheetData(true);

    const payload = {
      regenerate: true,
      invoice_date: invoiceDate,
      contract_id: row.contract_uuid,
      start_date: startDate,
      end_date: endDate
    };

    try {
      setIsRefreshing(true);
      const response = await InvoiceAPI.FETCH_INVOICES(axiosPrivate, payload);

      setDisplayAmount(response?.data?.data?.c2c_invoice_amount ?? 0);
      setApproverNames(response?.data?.data?.approver_names_list || []);
      setNonSubmittedNames(
        response?.data?.data?.non_submitted_names_list || []
      );
      setSubmittedNames(response?.data?.data?.submitted_names_list || []);
    } catch (error) {
      console.log("error while getting list ongoing timesheet", error);
    } finally {
      setIsRefreshing(false);
    }
  };

  // useEffect(() => {
  //   regenerateInvoice();
  // }, []);

  const handleChange = (event) => {
    setValue(event.target.value);
    // Clear date inputs and reset timesheet data when radio selection changes
    setStartDate("");
    setEndDate("");
    setShowTimesheetData(false);
    if (event.target.value === "milestone") {
      setAmount(parseFloat(row.c2c_milestone_amount));
      setApproverNames([]);
      setNonSubmittedNames([]);
      setSubmittedNames([]);
    } else if (event.target.value === "timesheet") {
      handleGetData();
      setAmount(parseFloat(row.c2c_timesheet_amount));
      setApproverNames(invoiceData.approver_names_list || []);
      setNonSubmittedNames(invoiceData.non_submitted_names_list || []);
      setSubmittedNames(invoiceData.submitted_names_list || []);
      setInvoiceDate(row.invoice_date);
      setStartDate(row.invoice_start_date);
      setEndDate(row.invoice_end_date);
    } else if (event.target.value === "manual") {
      setAmount(null);
      setApproverNames([]);
      setNonSubmittedNames([]);
      setSubmittedNames([]);
    }
  };

  const onGenerateClick = async () => {
    // Validate date order before submit
    const s = startDate ? new Date(startDate) : null;
    const e = endDate ? new Date(endDate) : null;
    if (s && e && s > e) {
      setDateError("Start date cannot be after end date.");
      return;
    }
    if (s && e && e < s) {
      setDateError("End date cannot be before start date.");
      return;
    }
    setDateError("");
    setIsGenerating(true);
    try {
      let payload = {
        client_id: selectedClient.uuid,
        invoice_id: row.c2c_invoice_id
      };

      if (value === "milestone") {
        payload.user_updated_amount = parseFloat(row.c2c_milestone_amount);
      } else if (value === "timesheet") {
        payload.user_updated_amount = parseFloat(row.c2c_timesheet_amount);
      } else if (value === "manual") {
        payload.user_updated_amount = amount;
      }

      const response = await InvoiceAPI.GENERATE_BY_AMOUNT(
        axiosPrivate,
        payload
      );
      if (response?.data) {
        enqueueSnackbar(translate("message.regenerate"), {
          variant: "success"
        });
        onClose();
        if (onRefreshData) {
          onRefreshData();
        }
      }
    } catch (error) {
      console.error("Error generating invoice:", error);
      enqueueSnackbar(translate("error.RegenerateFail"), { variant: "error" });
    } finally {
      setIsGenerating(false);
    }
  };

  const isGenerateButtonDisabled = () => {
    return value === "" || (value === "manual" && !amount) || isGenerating;
  };

  const isTimesheetComplete = row.c2c_resource_count === row.c2c_approved_count;

  const getTimesheetColor = () => {
    if (parseFloat(row.c2c_timesheet_amount) === 0) {
      return "#000000";
    }
    return isTimesheetComplete ? "#4caf50" : "#ff9800";
  };
  const isClientTab = localStorage.getItem("selectedTab") === "client";
  const clientEstimationLabels = labelsConfig.clientManagement.invoice.form;
  const budgetEstimationLabels = labelsConfig.budgetManagement.actuals.form;
  return (
    <Dialog open={true} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <Typography variant="h6">
            {isClientTab ? "Assign Invoice Amount" : "Assign Actual Amount"}
          </Typography>
          <Tooltip
            title={
              row.invoice_type === "invoice_type_1"
                ? "Cannot refresh data for milestone invoice"
                : ""
            }
            disableHoverListener={row.invoice_type !== "invoice_type_1"}
            arrow
            placement="top"
          >
            <span>
              <Button
                variant="outlined"
                size="small"
                startIcon={<RefreshIcon />}
                onClick={handleRefreshClick}
                disabled={isRefreshing || row.invoice_type === "invoice_type_1"}
              >
                Refresh Data
              </Button>
            </span>
          </Tooltip>
        </Stack>
      </DialogTitle>

      <DialogContent>
        {isRefreshing ? (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            minHeight="200px"
          >
            <CircularProgress />
          </Box>
        ) : (
          <>
            <Box sx={{ mt: 2 }}>
              <RadioGroup
                name="invoice-options"
                value={value}
                onChange={handleChange}
              >
                <FormControlLabel
                  value="milestone"
                  control={<Radio />}
                  label={
                    <Typography>
                      {isClientTab
                        ? `${
                            clientEstimationLabels.generateFromMilestoneAmount
                          } ($${
                            milestoneAmount === 0
                              ? row.c2c_milestone_amount
                              : milestoneAmount
                          })`
                        : `${
                            budgetEstimationLabels.generateFromActualAmount
                          } ($${
                            milestoneAmount === 0
                              ? row.c2c_milestone_amount
                              : milestoneAmount
                          })`}
                    </Typography>
                  }
                />

                <Tooltip
                  title={
                    row.invoice_type === "invoice_type_1"
                      ? "Cannot generate timesheet invoice as this is milestone invoice"
                      : ""
                  }
                  disableHoverListener={row.invoice_type !== "invoice_type_1"}
                  arrow
                  placement="left"
                >
                  {" "}
                  <FormControlLabel
                    value="timesheet"
                    control={<Radio />}
                    label={
                      <Box sx={{ display: "flex", alignItems: "center" }}>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            cursor:
                              row.invoice_type === "invoice_type_1"
                                ? "not-allowed"
                                : "default"
                          }}
                        >
                          <Typography>Timesheet Amount&nbsp;</Typography>
                          <Typography
                            sx={{
                              color: getTimesheetColor(),
                              fontWeight: "bold"
                            }}
                          >
                            {displayAmount}
                          </Typography>
                          {!isTimesheetComplete && (
                            <Tooltip
                              title={`Some resources have not submitted timesheet yet. Total Resources: ${row.c2c_resource_count}, Approved Timesheets: ${row.c2c_approved_count}`}
                              arrow
                              placement="top"
                            >
                              <InfoOutlinedIcon
                                sx={{
                                  ml: 1,
                                  color: getTimesheetColor(),
                                  fontSize: "1rem",
                                  cursor: "pointer"
                                }}
                              />
                            </Tooltip>
                          )}
                        </Box>
                      </Box>
                    }
                    disabled={row.invoice_type === "invoice_type_1"}
                  />
                </Tooltip>
                {value === "timesheet" && (
                  <>
                    {" "}
                    {true && (
                      <Box
                        sx={{
                          mt: 2,
                          mb: 2,
                          ml: 4, // Indent to align with radio button
                          p: 2,
                          backgroundColor: "#f9f9f9",
                          borderRadius: 2,
                          border: "1px solid #e0e0e0"
                        }}
                      >
                        <Typography
                          variant="subtitle1"
                          sx={{ fontWeight: "bold", color: "black" }}
                        >
                          Approvers:
                        </Typography>
                        <Typography sx={{ color: "gray" }}>
                          {approverNames.join(", ") || "None"}
                        </Typography>

                        <Typography
                          variant="subtitle1"
                          sx={{ fontWeight: "bold", color: "black", mt: 2 }}
                        >
                          Pending Timesheet Submissions:
                        </Typography>
                        <Typography sx={{ color: "gray" }}>
                          {nonSubmittedNames.join(", ") || "None"}
                        </Typography>

                        <Typography
                          variant="subtitle1"
                          sx={{ fontWeight: "bold", color: "black", mt: 2 }}
                        >
                          Submitted Timesheets:
                        </Typography>
                        <Typography sx={{ color: "gray" }}>
                          {submittedNames.join(", ") || "None"}
                        </Typography>
                      </Box>
                    )}
                    <Paper
                      elevation={0}
                      sx={{
                        p: 3,
                        mt: 1,
                        mb: 2,
                        ml: 4,
                        backgroundColor: "#f8f9fa",
                        borderRadius: 2,
                        border: "1px solid #e0e0e0"
                      }}
                    >
                      <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
                        <DatePicker
                          label="Invoice Date"
                          value={createDateForPicker(invoiceDate)}
                          onChange={(newValue) =>
                            handleDateChange(newValue, setInvoiceDate)
                          }
                          inputFormat="yyyy-MM-dd"
                          disabled={Boolean(row && row.invoice_date)}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              fullWidth
                              placeholder="yyyy-mm-dd"
                              sx={{ minWidth: 200, mb: 2 }}
                              disabled={Boolean(row && row.invoice_date)}
                            />
                          )}
                        />

                        <DatePicker
                          label="Start Date"
                          value={createDateForPicker(startDate)}
                          onChange={(newValue) => {
                            handleDateChange(newValue, setStartDate);
                            // Validate dates
                            const s = newValue ? new Date(newValue) : null;
                            const e = endDate ? new Date(endDate) : null;
                            if (s && e && s > e) {
                              setDateError(
                                "Start date cannot be after end date."
                              );
                            } else {
                              setDateError("");
                            }
                          }}
                          inputFormat="yyyy-MM-dd"
                          disabled={Boolean(row && row.invoice_start_date)}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              fullWidth
                              margin="dense"
                              placeholder="yyyy-mm-dd"
                              sx={{ minWidth: 180, width: "100%" }}
                              disabled={Boolean(row && row.invoice_start_date)}
                            />
                          )}
                        />

                        <DatePicker
                          label="End Date"
                          value={createDateForPicker(endDate)}
                          onChange={(newValue) => {
                            handleDateChange(newValue, setEndDate);
                            // Validate dates
                            const e = newValue ? new Date(newValue) : null;
                            const s = startDate ? new Date(startDate) : null;
                            if (s && e && e < s) {
                              setDateError(
                                "End date cannot be before start date."
                              );
                            } else {
                              setDateError("");
                            }
                          }}
                          inputFormat="yyyy-MM-dd"
                          disabled={Boolean(row && row.invoice_end_date)}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              fullWidth
                              margin="dense"
                              placeholder="yyyy-mm-dd"
                              sx={{ minWidth: 180, width: "100%" }}
                              disabled={Boolean(row && row.invoice_end_date)}
                            />
                          )}
                        />

                        <Button
                          variant="contained"
                          onClick={handleGetData}
                          sx={{
                            height: "56px",
                            minWidth: 160,
                            whiteSpace: "nowrap"
                          }}
                        >
                          Regenerate Timesheet Data
                        </Button>
                      </Box>
                    </Paper>
                    {dateError && (
                      <div style={{ color: "red", marginTop: 8 }}>
                        {dateError}
                      </div>
                    )}
                  </>
                )}

                <FormControlLabel
                  value="manual"
                  control={<Radio />}
                  label={<Typography>Customized Amount</Typography>}
                />
              </RadioGroup>
            </Box>
            {value === "manual" && (
              <Paper
                elevation={0}
                sx={{
                  p: 3,
                  mt: 3,
                  backgroundColor: "#f8f9fa",
                  borderRadius: 2,
                  border: "1px solid #e0e0e0"
                }}
              >
                <Typography
                  variant="subtitle1"
                  mb={2}
                  fontWeight="medium"
                  gutterBottom
                >
                  Enter Invoice Amount
                </Typography>
                <TextField
                  fullWidth
                  label="Amount"
                  variant="outlined"
                  type="number"
                  value={amount || ""}
                  onChange={(e) => setAmount(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <AttachMoneyIcon color="action" />
                      </InputAdornment>
                    )
                  }}
                  sx={{
                    backgroundColor: "white",
                    "& .MuiOutlinedInput-root": {
                      "&:hover fieldset": {
                        borderColor: "primary.main"
                      }
                    }
                  }}
                />
              </Paper>
            )}
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 2, pt: 0 }}>
        <Button variant="outlined" onClick={onClose}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={onGenerateClick}
          disabled={isGenerateButtonDisabled() || dateError !== ""}
        >
          {isGenerating ? "Generating..." : "Generate"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
